import { useState, useEffect, useRef, useCallback } from 'react';

const DS5_VENDOR_ID  = 0x054c;
const DS5_PRODUCT_ID = 0x0ce6;
const EXPECTED_SERIAL_LENGTH = 17;

const LIGHTBAR_KEEPALIVE_MS = 3_000;

// ─── Lightbar color presets ───────────────────────────────────────────────────
export const LIGHTBAR_COLOR = {
  OFF:   [0,   0,   0  ],
  GREEN: [0,   255, 0  ],
  RED:   [255, 0,   0  ],
  WHITE: [255, 255, 255],
  BLUE:  [0,   0,   255],
};

// ─── Build USB output report ──────────────────────────────────────────────────
//
// Confirmed from real Wireshark/USB capture (yesbotics/dualsense-controller-python):
//
//   Full 48-byte USB packet on wire:
//   02 ff f7 00 00 00 00 00 00 00 10 [triggers x14] 00 00 02 00 02 00 00 R  G  B
//   ^reportId
//      ^[0] flags_physics  = 0xFF   (REQUIRED — controller drops report if 0)
//         ^[1] flags_controls = 0xF7   (enables lightbar)
//                                    ^[9]  microphone_mute = 0x10
//                                             ^[40] lightbar_pulse_options = 0x02
//                                                               ^[44] R
//                                                                  ^[45] G
//                                                                     ^[46] B
//
// WebHID sendReport(reportId, data):
//   reportId = 0x02  (NOT included in data array)
//   data     = 47 bytes  (the common payload without the leading 0x02 report-ID byte)
//
// Payload indices (0-based, report-ID is separate):
//   [0]  = 0xFF  flags_physics
//   [1]  = 0xF7  flags_controls
//   [9]  = 0x10  microphone_mute  (keeps mic LED from flickering)
//   [40] = 0x02  lightbar_pulse_options  (direct RGB, disables animation)
//   [44] = R
//   [45] = G
//   [46] = B
//
const buildLightbarReport = (r, g, b) => {
  const data = new Uint8Array(47);
  data[0]  = 0xFF; // flags_physics  — controller ignores report if this is 0
  data[1]  = 0xF7; // flags_controls — bit that enables lightbar
  data[9]  = 0x10; // microphone_mute — prevents mic LED side-effects
  data[40] = 0x02; // lightbar_pulse_options — direct RGB mode, no animation
  data[44] = r;
  data[45] = g;
  data[46] = b;
  return data;
};

const sendLightbarToDevice = async (device, r, g, b) => {
  if (!device || !device.opened) return false;
  try {
    await device.sendReport(0x02, buildLightbarReport(r, g, b));
    return true;
  } catch (e) {
    console.warn('[DS5] sendReport failed:', e);
    return false;
  }
};

// ─── Pure helpers ─────────────────────────────────────────────────────────────
const hwToBoardModel = (hw_ver) => {
  const a = (hw_ver >> 8) & 0xff;
  if (a === 0x03) return "BDM-010";
  if (a === 0x04) return "BDM-020";
  if (a === 0x05) return "BDM-030";
  if (a === 0x06) return "BDM-040";
  if (a === 0x07 || a === 0x08) return "BDM-050";
  return "Unknown";
};

const ds5_color = (serialNumber) => {
  const colorMap = {
    '00': 'White',           '01': 'Black',          '02': 'Cosmic Red',
    '03': 'Nova Pink',       '04': 'Galactic Purple', '05': 'Starlight Blue',
    '06': 'Grey Camouflage', '07': 'Volcanic Red',    '08': 'Sterling Silver',
    '09': 'Cobalt Blue',     '10': 'Chroma Teal',     '11': 'Chroma Indigo',
    '12': 'Chroma Pearl',    '30': '30th Anniversary', 'Z1': 'God of War Ragnarok',
    'Z2': 'Spider-Man 2',    'Z3': 'Astro Bot',        'Z4': 'Fortnite',
    'Z6': 'The Last of Us',  'ZB': 'Icon Blue Limited Edition',
  };
  return colorMap[serialNumber.slice(4, 6)] || 'Unknown';
};

const readSerialNumber = async (device) => {
  try {
    const report20 = await device.receiveFeatureReport(0x20);
    if (report20.buffer.byteLength < 64 || new DataView(report20.buffer).getUint8(0) !== 0x20) return null;

    const request = new Uint8Array(64);
    request[0] = 1;
    request[1] = 19;
    await device.sendFeatureReport(0x80, request);

    const report = await device.receiveFeatureReport(0x81);
    const view   = new DataView(report.buffer);
    if (view.getUint8(1) !== 1 || view.getUint8(2) !== 19 || view.getUint8(3) !== 2) return null;

    const data   = new Uint8Array(report.buffer.slice(4, 21));
    const serial = new TextDecoder().decode(data).replace(/\0/g, '').trim();
    if (serial.length !== EXPECTED_SERIAL_LENGTH) return null;

    const cleaned = serial.replace(/[^\w]/g, '').toUpperCase();
    if (cleaned.length !== EXPECTED_SERIAL_LENGTH) return null;
    if (!/^[A-Z0-9]{17}$/.test(cleaned)) return null;
    return cleaned;
  } catch (e) {
    console.error('[DS5] Serial read error:', e);
    return null;
  }
};

const getBoardModel = async (device) => {
  try {
    const report  = await device.receiveFeatureReport(0x20);
    const hwModel = report.getUint32(24, true);
    return hwToBoardModel(hwModel);
  } catch {
    return "Unknown";
  }
};

// ─── Hook ─────────────────────────────────────────────────────────────────────
export const useDS5Controller = () => {
  const [controllers, setControllers] = useState([]);
  const [isLoading,   setIsLoading]   = useState(true);
  const [globalError, setGlobalError] = useState('');

  // deviceKey → { device, handleInputReport, currentRgb, keepaliveTimer }
  const devicesRef       = useRef(new Map());
  const scanIntervalRef  = useRef(null);
  const hasInteractedRef = useRef(false);
  const deviceKeyMap     = useRef(new WeakMap());
  const deviceKeyCtr     = useRef(0);

  const isWebHIDSupported = 'hid' in navigator;

  const getDeviceKey = (device) => {
    if (!deviceKeyMap.current.has(device))
      deviceKeyMap.current.set(device, ++deviceKeyCtr.current);
    return deviceKeyMap.current.get(device);
  };

  const updateController = useCallback((deviceKey, patch) => {
    setControllers(prev => {
      const exists = prev.find(c => c.id === deviceKey);
      if (!exists) {
        return [...prev, {
          id: deviceKey, serialNumber: '', hasValidSerial: false,
          deviceInfo: '', rawData: null, boardModel: '', ds5color: '', error: '',
          ...patch,
        }];
      }
      return prev.map(c => c.id === deviceKey ? { ...c, ...patch } : c);
    });
  }, []);

  const removeController = useCallback((deviceKey) => {
    setControllers(prev => prev.filter(c => c.id !== deviceKey));
  }, []);

  // ── Keepalive ─────────────────────────────────────────────────────────────
  const stopKeepalive = useCallback((deviceKey) => {
    const e = devicesRef.current.get(deviceKey);
    if (e?.keepaliveTimer) {
      clearInterval(e.keepaliveTimer);
      e.keepaliveTimer = null;
    }
  }, []);

  const startKeepalive = useCallback((deviceKey) => {
    stopKeepalive(deviceKey);
    const timerId = setInterval(async () => {
      const e = devicesRef.current.get(deviceKey);
      if (!e?.currentRgb) return;
    }, LIGHTBAR_KEEPALIVE_MS);
    const e = devicesRef.current.get(deviceKey);
    if (e) e.keepaliveTimer = timerId;
  }, [stopKeepalive]);

  // ── Public: set lightbar for one controller ───────────────────────────────
  const setControllerLightbar = useCallback(async (deviceKey, r, g, b) => {
    const e = devicesRef.current.get(deviceKey);
    if (!e) {
      console.warn('[DS5] setControllerLightbar: unknown key', deviceKey);
      return;
    }
    e.currentRgb = [r, g, b];
    const ok = await sendLightbarToDevice(e.device, r, g, b);
    console.log(`[DS5] Lightbar [key=${deviceKey}] rgb(${r},${g},${b}) sent=${ok}`);
  }, []);

  // ── Connect ───────────────────────────────────────────────────────────────
  const connect = useCallback(async (device) => {
    const deviceKey = getDeviceKey(device);
    if (devicesRef.current.has(deviceKey)) return;

    devicesRef.current.set(deviceKey, {
      device,
      handleInputReport: null,
      currentRgb:        [...LIGHTBAR_COLOR.BLUE],
      keepaliveTimer:    null,
    });

    try {
      if (!device.opened) await device.open();

      // Send WHITE immediately — light is physically on from the first frame
      await sendLightbarToDevice(device, ...LIGHTBAR_COLOR.BLUE);
      startKeepalive(deviceKey);

      const serial     = await readSerialNumber(device);
      const boardModel = await getBoardModel(device);

      const handleInputReport = (event) => {
        updateController(deviceKey, { rawData: new Uint8Array(event.data.buffer) });
      };
      device.addEventListener('inputreport', handleInputReport);
      devicesRef.current.get(deviceKey).handleInputReport = handleInputReport;

      if (!serial) {
        updateController(deviceKey, {
          serialNumber: '', hasValidSerial: false,
          deviceInfo: 'DualSense (Serial invalide)', boardModel, ds5color: '',
          error: `Manette connectée mais serial number (${EXPECTED_SERIAL_LENGTH} car.) illisible. Reconnectez-la.`,
        });
        return; // stays WHITE
      }

      updateController(deviceKey, {
        serialNumber: serial, hasValidSerial: true,
        deviceInfo: device.productName || 'DualSense',
        boardModel, ds5color: ds5_color(serial), error: '',
      });

      console.log(`[DS5] Connected [key=${deviceKey}] serial: ${serial}`);
    } catch (err) {
      console.error('[DS5] Connection failed:', err);
      updateController(deviceKey, { error: 'Connexion échouée: ' + err.message, hasValidSerial: false });
    }
  }, [updateController, startKeepalive]);

  const scanDevices = useCallback(async () => {
    if (!isWebHIDSupported) return;
    try {
      const devices    = await navigator.hid.getDevices();
      const ds5Devices = devices.filter(d => d.vendorId === DS5_VENDOR_ID && d.productId === DS5_PRODUCT_ID);

      if (ds5Devices.length > 0) {
        for (const d of ds5Devices) await connect(d);
        setIsLoading(false);
      } else if (!hasInteractedRef.current) {
        try {
          const granted = await navigator.hid.requestDevice({
            filters: [{ vendorId: DS5_VENDOR_ID, productId: DS5_PRODUCT_ID }]
          });
          for (const d of granted) await connect(d);
        } catch (e) {
          console.log('[DS5] Request cancelled:', e);
        }
        hasInteractedRef.current = true;
        setIsLoading(false);
      }
    } catch (err) {
      console.error('[DS5] Scan error:', err);
      setGlobalError('Erreur de scan: ' + err.message);
      setIsLoading(false);
    }
  }, [connect, isWebHIDSupported]);

  useEffect(() => {
    if (!isWebHIDSupported) { setIsLoading(false); return; }
    scanDevices();
    scanIntervalRef.current = setInterval(scanDevices, 2_000);
    return () => {
      if (scanIntervalRef.current) clearInterval(scanIntervalRef.current);
      devicesRef.current.forEach(({ device, handleInputReport, keepaliveTimer }) => {
        if (keepaliveTimer)    clearInterval(keepaliveTimer);
        if (handleInputReport) device.removeEventListener('inputreport', handleInputReport);
      });
    };
  }, [isWebHIDSupported, scanDevices]);

  useEffect(() => {
    if (!isWebHIDSupported) return;
    const onDisconnect = (e) => {
      const deviceKey = getDeviceKey(e.device);
      const entry     = devicesRef.current.get(deviceKey);
      if (entry) {
        stopKeepalive(deviceKey);
        if (entry.handleInputReport)
          e.device.removeEventListener('inputreport', entry.handleInputReport);
        devicesRef.current.delete(deviceKey);
        removeController(deviceKey);
        console.log(`[DS5] Disconnected [key=${deviceKey}]`);
      }
    };
    navigator.hid.addEventListener('disconnect', onDisconnect);
    return () => navigator.hid.removeEventListener('disconnect', onDisconnect);
  }, [isWebHIDSupported, removeController, stopKeepalive]);

  const disconnect = useCallback((deviceKey) => {
    const entry = devicesRef.current.get(deviceKey);
    if (entry) {
      stopKeepalive(deviceKey);
      try {
        if (entry.handleInputReport)
          entry.device.removeEventListener('inputreport', entry.handleInputReport);
        entry.device.close();
      } catch (e) { console.error('[DS5] Close error:', e); }
      devicesRef.current.delete(deviceKey);
    }
    removeController(deviceKey);
  }, [removeController, stopKeepalive]);

  const disconnectAll = useCallback(() => {
    devicesRef.current.forEach((_, key) => disconnect(key));
    hasInteractedRef.current = false;
  }, [disconnect]);

  const first = controllers[0] || null;

  return {
    controllers,
    disconnect,
    disconnectAll,
    setControllerLightbar,

    // backward compat
    serialNumber:     first?.serialNumber   ?? '',
    isConnected:      controllers.length > 0,
    error:            first?.error          ?? globalError,
    isLoading,
    deviceInfo:       first?.deviceInfo     ?? '',
    isWebHIDSupported,
    rawData:          first?.rawData        ?? null,
    hasValidSerial:   first?.hasValidSerial ?? false,
    BoardModel:       first?.boardModel     ?? '',
    ds5color:         first?.ds5color       ?? '',
  };
};